import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { Eyebrow, Btn, SectionShell } from "@/components/site/Primitives";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { scrollToId } from "@/lib/site";
import { track } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AREA_OPTIONS = [
  "Contract Intelligence",
  "Financial Risk Intelligence",
  "Market Intelligence",
  "Portfolio Intelligence",
  "Negotiation Intelligence",
  "Executive Decision Intelligence",
  "Enterprise Pilot",
  "Strategic Partnership",
  "Investor Enquiry",
  "Other",
];

const schema = z.object({
  fullName: z.string().min(2, "Please enter your full name."),
  workEmail: z.string().min(1, "Work email is required.").email("Enter a valid email address."),
  company: z.string().min(1, "Company is required."),
  role: z.string().min(1, "Role is required."),
  areaOfInterest: z.string().min(1, "Please select an area of interest."),
  challenge: z.string().min(10, "Please describe your challenge or use case (min. 10 characters)."),
  consent: z.literal(true, { errorMap: () => ({ message: "Consent is required to proceed." }) }),
});

const inputCls =
  "h-11 border-white/20 bg-white/[0.04] text-natsu-warm-white placeholder:text-natsu-warm-white/50 focus-visible:ring-natsu-gold/60";

function ErrorText({ children, id }) {
  return (
    <p id={id} className="mt-1.5 flex items-center gap-1.5 text-[12px] text-natsu-risk" role="alert">
      <AlertCircle className="h-3.5 w-3.5" /> {children}
    </p>
  );
}

function ReqLabel({ htmlFor, children }) {
  return (
    <Label htmlFor={htmlFor} className="text-[12.5px] font-medium text-natsu-warm-white/80">
      {children} <span className="text-natsu-gold">*</span>
    </Label>
  );
}

export function PilotEnquiry() {
  const [submitState, setSubmitState] = useState("idle"); // idle | submitting | success | error
  const [startedFired, setStartedFired] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isValid, isSubmitted },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      fullName: "",
      workEmail: "",
      company: "",
      role: "",
      areaOfInterest: "",
      challenge: "",
      consent: false,
    },
  });

  const onSubmit = async (values) => {
    if (submitState === "submitting" || submitState === "success") return;
    setSubmitState("submitting");
    try {
      const res = await fetch(`${BACKEND_URL}/api/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          enquiryType: values.areaOfInterest === "Investor Enquiry" ? "Investor Enquiry" : "Pilot Enquiry",
          pageSource: typeof window !== "undefined" ? window.location.pathname + window.location.hash : "",
        }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      setSubmitState("success");
      track("form_submit_success");
      // scroll to success message
      window.setTimeout(() => scrollToId("pilot"), 60);
    } catch (err) {
      setSubmitState("error");
      track("form_submit_failed");
    }
  };

  const onFormFocus = () => {
    if (!startedFired) {
      track("form_started");
      setStartedFired(true);
    }
  };

  useEffect(() => {
    // preselect Investor Enquiry if arrived via hash
    if (typeof window !== "undefined" && window.location.hash === "#investor") {
      setValue("areaOfInterest", "Investor Enquiry", { shouldValidate: true });
    }
  }, [setValue]);

  return (
    <SectionShell id="pilot" tone="dark" grain prominent>
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <div>
          <Eyebrow tone="dark" color="gold">Request a Pilot</Eyebrow>
          <h2 className="display mt-6 text-3xl leading-[1.14] text-natsu-warm-white sm:text-4xl">
            Identify one commercial decision where better intelligence could create
            meaningful value.
          </h2>
          <p className="mt-6 measure text-[15px] leading-relaxed text-natsu-warm-white/70">
            Tell us about your decision environment, priorities and potential use
            case. NATSU Intelligence will review the enquiry and assess whether a
            focused pilot is appropriate.
          </p>
        </div>

        <div className="rounded-2xl border border-white/12 bg-white/[0.02] p-6 sm:p-8" data-testid="pilot-form">
          {submitState === "success" ? (
            <div className="flex min-h-[320px] flex-col items-start text-left" data-testid="pilot-success">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-natsu-green/15 ring-1 ring-natsu-green/30">
                <CheckCircle2 className="h-7 w-7 text-natsu-green" />
              </span>
              <h3 className="mt-6 font-display text-2xl leading-tight text-natsu-warm-white sm:text-3xl">
                Thank you. Your enquiry has been received.
              </h3>
              <p className="mt-4 max-w-[52ch] text-[14.5px] leading-relaxed text-natsu-warm-white/75">
                NATSU Intelligence will review the information provided and assess
                whether a focused pilot or further discussion is appropriate. Where
                relevant, we will respond using the work email address supplied.
              </p>
              <Btn
                variant="gold"
                onClick={() => scrollToId("what-natsu")}
                withArrow
                testId="pilot-return-platform"
                className="mt-7"
              >
                Return to Platform
              </Btn>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} onFocus={onFormFocus} noValidate>
              {/* Honeypot — visually and semantically hidden from users */}
              <div aria-hidden="true" className="pointer-events-none absolute -left-[9999px] h-0 w-0 overflow-hidden">
                <label>
                  Do not fill this field
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    {...register("website")}
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <ReqLabel htmlFor="fullName">Full Name</ReqLabel>
                  <Input id="fullName" data-testid="field-fullName" className={`mt-2 ${inputCls}`} placeholder="Your full name" aria-invalid={!!errors.fullName} {...register("fullName")} />
                  {errors.fullName && <ErrorText>{errors.fullName.message}</ErrorText>}
                </div>
                <div>
                  <ReqLabel htmlFor="workEmail">Work Email</ReqLabel>
                  <Input id="workEmail" type="email" data-testid="field-workEmail" className={`mt-2 ${inputCls}`} placeholder="name@company.com" aria-invalid={!!errors.workEmail} {...register("workEmail")} />
                  {errors.workEmail && <ErrorText>{errors.workEmail.message}</ErrorText>}
                </div>
                <div>
                  <ReqLabel htmlFor="company">Company</ReqLabel>
                  <Input id="company" data-testid="field-company" className={`mt-2 ${inputCls}`} placeholder="Company name" aria-invalid={!!errors.company} {...register("company")} />
                  {errors.company && <ErrorText>{errors.company.message}</ErrorText>}
                </div>
                <div>
                  <ReqLabel htmlFor="role">Role</ReqLabel>
                  <Input id="role" data-testid="field-role" className={`mt-2 ${inputCls}`} placeholder="Your role" aria-invalid={!!errors.role} {...register("role")} />
                  {errors.role && <ErrorText>{errors.role.message}</ErrorText>}
                </div>
              </div>

              <div className="mt-5">
                <ReqLabel htmlFor="areaOfInterest">Area of Interest</ReqLabel>
                <Controller
                  name="areaOfInterest"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="areaOfInterest" data-testid="field-areaOfInterest" className={`mt-2 ${inputCls}`} aria-invalid={!!errors.areaOfInterest}>
                        <SelectValue placeholder="Select an area of interest" />
                      </SelectTrigger>
                      <SelectContent>
                        {AREA_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.areaOfInterest && <ErrorText>{errors.areaOfInterest.message}</ErrorText>}
              </div>

              <div className="mt-5">
                <ReqLabel htmlFor="challenge">Commercial Challenge or Pilot Use Case</ReqLabel>
                <Textarea id="challenge" data-testid="field-challenge" rows={4} className={`mt-2 border-white/20 bg-white/[0.04] text-natsu-warm-white placeholder:text-natsu-warm-white/50 focus-visible:ring-natsu-gold/60`} placeholder="Describe the decision environment and potential use case…" aria-invalid={!!errors.challenge} {...register("challenge")} />
                {errors.challenge && <ErrorText>{errors.challenge.message}</ErrorText>}
              </div>

              <div className="mt-5">
                <Controller
                  name="consent"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-start gap-3">
                      <Checkbox id="consent" data-testid="field-consent" checked={field.value} onCheckedChange={field.onChange} className="mt-0.5 border-white/40 data-[state=checked]:bg-natsu-gold data-[state=checked]:border-natsu-gold data-[state=checked]:text-natsu-navy" />
                      <Label htmlFor="consent" className="text-[12.5px] font-normal leading-relaxed text-natsu-warm-white/80">
                        I consent to NATSU Intelligence using the information submitted through this form to review and respond to my enquiry. I understand that my information will be handled in accordance with the{" "}
                        <Link
                          to="/privacy"
                          onClick={() => track("privacy_policy_opened")}
                          className="text-natsu-blue-soft underline underline-offset-2 hover:text-natsu-warm-white"
                        >
                          Privacy Policy
                        </Link>
                        . I may withdraw my consent by contacting moustakas@natsuintelligence.com.
                      </Label>
                    </div>
                  )}
                />
                {errors.consent && <ErrorText>{errors.consent.message}</ErrorText>}
              </div>

              {isSubmitted && Object.keys(errors).length > 0 && (
                <p className="mt-5 flex items-center gap-2 rounded-lg border border-natsu-risk/25 bg-natsu-risk/[0.06] px-3.5 py-2.5 text-[12.5px] text-natsu-risk" role="alert">
                  <AlertCircle className="h-4 w-4" /> Please correct the highlighted fields.
                </p>
              )}

              {submitState === "error" && (
                <p className="mt-5 flex items-start gap-2 rounded-lg border border-natsu-risk/25 bg-natsu-risk/[0.06] px-3.5 py-2.5 text-[12.5px] leading-relaxed text-natsu-risk" role="alert" data-testid="pilot-error">
                  <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  We could not submit your enquiry at this time. Please try again or contact{" "}
                  <a href="mailto:moustakas@natsuintelligence.com" className="underline underline-offset-2">
                    moustakas@natsuintelligence.com
                  </a>
                  .
                </p>
              )}

              <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center">
                <Btn
                  variant="gold"
                  type="submit"
                  withArrow
                  testId="pilot-submit"
                  className="w-full sm:w-auto disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!isValid || submitState === "submitting"}
                >
                  {submitState === "submitting" ? "Submitting…" : "Request a Pilot"}
                </Btn>
                <span className="text-[13px] text-natsu-warm-white/70">
                  or{" "}
                  <button
                    type="button"
                    data-testid="investor-enquiries"
                    onClick={() => {
                      setValue("areaOfInterest", "Investor Enquiry", { shouldValidate: true });
                      track("investor_enquiries_clicked");
                    }}
                    className="text-natsu-blue-soft underline underline-offset-4 hover:text-natsu-warm-white"
                  >
                    Investor Enquiries
                  </button>
                </span>
              </div>
              <p className="mt-4 text-[12px] text-natsu-warm-white/55">
                We will use this information only to review and respond to your enquiry.
              </p>
            </form>
          )}
        </div>
      </div>
    </SectionShell>
  );
}
